/**
 * Quality detection. Picks a level from the GPU string, pixel ratio, screen size, cores and mobile-ness.
 * ?q=mobile|low|medium|high|ultra overrides; mobile devices keep their bounded tier.
 */
import { DETERMINISTIC } from './determinism';
import type { Quality } from './context';

export type QualityLevel = Quality['level'];

/**
 * The quality ladder. Documented, with the measurements behind it, in docs/ART_DIRECTION.md §10.
 *
 * What the frame actually costs (measured, M2 Max / Metal, three spots, 1080p and 1440p): frame time
 * tracks DRAW CALLS almost linearly (~7 us each) and barely moves with resolution. So the rungs are
 * bought with draw calls -- shadow cascades, draw distance, crowd and traffic caps -- and the
 * screen-space passes that cost pixels rather than draws (AO at half res, bloom's mip chain, a full
 * pixel ratio) are cheap enough to keep well below ultra. That is why medium keeps the cues that make
 * a frame read as a photograph (sharp image, contact shade, signs that glow at night) and pays for
 * them with reach and cascade count, which read as almost nothing.
 *
 * ultra is the reference look and never moves.
 */
const TABLE: Record<QualityLevel, Omit<Quality, 'level' | 'pixelRatio'> & { pixelRatio: (dpr: number) => number; maxPedHeads: number; maxPedPoses: number }> = {
  mobile: { mobile: true, pixelRatio: () => 0.75, shadows: false, shadowMapSize: 256, ssao: false, bloom: false, reflections: false, drawDistance: 320, farDistance: 1500, maxTraffic: 8, maxPeds: 12, maxPedHeads: 0, maxPedPoses: 0 },
  ultra: { pixelRatio: (d) => Math.min(d, 2), shadows: true, shadowMapSize: 4096, ssao: true, bloom: true, reflections: true, drawDistance: 1200, farDistance: 6000, maxTraffic: 80, maxPeds: 220, maxPedHeads: 16, maxPedPoses: 12 },
  high: { pixelRatio: (d) => Math.min(d, 1.5), shadows: true, shadowMapSize: 2048, ssao: true, bloom: true, reflections: false, drawDistance: 1000, farDistance: 5000, maxTraffic: 60, maxPeds: 160, maxPedHeads: 16, maxPedPoses: 8 },
  // Medium renders at native 1080p, keeps AO and bloom, and carries a real crowd. It pays for that
  // with half of high's shadow map, a 200 m cascade reach, 700 m of detailed city and no SSR.
  medium: { pixelRatio: (d) => Math.min(d, 1), shadows: true, shadowMapSize: 1024, ssao: true, bloom: true, reflections: false, drawDistance: 700, farDistance: 3000, maxTraffic: 50, maxPeds: 90, maxPedHeads: 8, maxPedPoses: 8 },
  low: { pixelRatio: () => 0.75, shadows: true, shadowMapSize: 512, ssao: false, bloom: false, reflections: false, drawDistance: 600, farDistance: 2500, maxTraffic: 20, maxPeds: 20, maxPedHeads: 4, maxPedPoses: 4 },
};

/** Nearest full rigs with imported heads; follows the live tier, including startup's cheaper rung. */
export function pedHeadBudget(level: QualityLevel): number { return TABLE[level].maxPedHeads; }
/** Full-rate poses retain the desktop animation budget; additional imported heads keep the mid-LOD 20 Hz cadence. */
export function pedPoseBudget(level: QualityLevel): number { return TABLE[level].maxPedPoses; }

export interface DeviceInfo {
  gpu: string;
  vendor: string;
  dpr: number;
  screenW: number;
  screenH: number;
  cores: number;
  mobile: boolean;
  software: boolean;
  webgl2: boolean;
}

/** CPU-only detection: safe to run before the entry form, including iPad desktop UA. */
export function isIOS(): boolean {
  return typeof navigator !== 'undefined' && (/iPad|iPhone|iPod/i.test(navigator.userAgent) ||
    (/Mac/i.test(navigator.userAgent + navigator.platform) && navigator.maxTouchPoints > 0));
}

export function isMobileDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  const memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  return isIOS() || /Android|Mobile|Silk/i.test(navigator.userAgent) ||
    (typeof memory === 'number' && memory <= 4) ||
    (typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches);
}

/** Never fetch/decode the desktop 2K images on mobile. Keep manifest metadata unchanged. */
export function mobileTextureUrl(url: string): string {
  return isMobileDevice() && /\/assets\/textures\/.*\.jpg(?:[?#]|$)/i.test(url)
    ? url.replace('/assets/textures/', '/assets/textures-mobile/') : url;
}

export function mobilePixelRatio(dpr: number, width: number, height: number): number {
  return Math.min(dpr, 0.75, Math.sqrt(650_000 / Math.max(1, width * height)));
}

export function probeDevice(): DeviceInfo {
  let gpu = 'unknown';
  let vendor = 'unknown';
  let webgl2 = false;
  try {
    if (isIOS()) throw new Error('iOS uses the conservative tier without a GPU probe');
    const c = document.createElement('canvas');
    const gl = (c.getContext('webgl2') || c.getContext('webgl')) as WebGLRenderingContext | null;
    if (gl) {
      webgl2 = gl instanceof WebGL2RenderingContext;
      const ext = gl.getExtension('WEBGL_debug_renderer_info');
      if (ext) {
        gpu = String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL));
        vendor = String(gl.getParameter(ext.UNMASKED_VENDOR_WEBGL));
      } else {
        gpu = String(gl.getParameter(gl.RENDERER));
        vendor = String(gl.getParameter(gl.VENDOR));
      }
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    }
  } catch {
    /* no webgl: treated as software below */
  }
  const mobile = isMobileDevice();
  const software = /swiftshader|llvmpipe|software|mesa offscreen|basic render/i.test(gpu);
  return { gpu, vendor, dpr: window.devicePixelRatio || 1, screenW: screen.width, screenH: screen.height, cores: navigator.hardwareConcurrency || 4, mobile, software, webgl2 };
}

/** heuristic level from a device probe */
export function levelFor(d: DeviceInfo): QualityLevel {
  const g = d.gpu.toLowerCase();
  if (d.mobile) return 'mobile';
  if (d.software) return 'low';
  // "laptop"/"mobile"/"Max-Q" in the renderer string is a real signal: the same die runs at a
  // fraction of the desktop part's power budget. Anything mobile lands a rung lower than its name.
  const laptop = /laptop|mobile|notebook|max-q/.test(g);
  // Apple silicon. The Pro/Max/Ultra dies are desktop-class; the base M1/M2/M3/M4 in an Air
  // measures like an integrated laptop GPU and belongs on medium. (Measured: M2 Max holds
  // high at 1080p with margin; ultra's 4x4096 cascades + full-res SSR do not fit 60 Hz there.)
  if (/apple m\d|apple gpu/.test(g)) {
    if (/apple m\d+\s*ultra/.test(g)) return 'ultra';
    if (/apple m\d+\s*(pro|max)/.test(g)) return 'high';
    return 'medium';
  }
  // NVIDIA
  if (/geforce.*rtx\s*[3-9]\d{3}/.test(g)) return laptop ? 'high' : 'ultra';
  if (/rtx\s*20|rtx\s*2\d{2}|gtx\s*1[06-9]|gtx\s*[2-9]0\d{1,2}|rtx\s*a/.test(g)) return laptop ? 'medium' : 'high';
  if (/geforce|nvidia/.test(g)) return 'medium';
  // AMD
  if (/radeon rx\s*[6-9]\d{3}\b/.test(g) && !laptop) return 'ultra';
  if (/radeon rx|radeon\(tm\) rx|amd radeon/.test(g)) return laptop ? 'medium' : 'high';
  if (/radeon|amd/.test(g)) return 'medium';
  // Intel
  if (/intel.*(arc)/.test(g)) return laptop ? 'medium' : 'high';
  if (/intel.*(iris|xe)/.test(g)) return 'medium';
  if (/intel/.test(g)) return 'low';
  // Unknown GPU: medium is the safe rung -- it holds 60 fps at 1080p on laptop-class hardware and
  // the pause menu still lets anyone move up.
  if (d.cores >= 4) return 'medium';
  return 'low';
}

export function buildQuality(level: QualityLevel, dpr: number): Quality {
  const t = TABLE[level];
  const iosCaps = level === 'mobile' && isIOS();
  return { level, ...(level === 'mobile' ? { mobile: true } : {}), pixelRatio: t.pixelRatio(dpr), shadows: t.shadows, shadowMapSize: t.shadowMapSize, ssao: t.ssao, bloom: t.bloom, reflections: t.reflections, drawDistance: t.drawDistance, farDistance: iosCaps ? t.drawDistance : t.farDistance, maxTraffic: iosCaps ? 6 : t.maxTraffic, maxPeds: iosCaps ? 8 : t.maxPeds };
}

export function isQualityLevel(s: string | null | undefined): s is QualityLevel {
  return s === 'mobile' || s === 'low' || s === 'medium' || s === 'high' || s === 'ultra';
}

/** No distant city cache on phones: near street detail, skyline landmarks to 1.5 km. */
export function mobileQuality(): Quality { return buildQuality('mobile', 1); }

export function detectQuality(override?: string | null): { quality: Quality; device: DeviceInfo; auto: QualityLevel } {
  const device = probeDevice();
  const auto = levelFor(device);
  const level: QualityLevel = device.mobile ? 'mobile' : isQualityLevel(override) ? override : auto;
  const quality = buildQuality(level, device.dpr);
  // Depth pre-pass (atmosphere/prepass.ts): the static city writes depth first with no fragment work, so the
  // colour pass shades only what is visible. Measured at 1280x720 on an M2 Max: Bryant Park 17.3 -> 22.3 fps,
  // Times Square 25 -> 30.3, with at most ~100 of 921,600 pixels differing (multisampled ties along the horizon).
  // Off on mobile, and off for deterministic pages so capture rigs and byte-identical gates keep the single pass.
  // `?fx=noprepass` to A/B it; `window.__ppDiff()` renders both ways in one frame and compares raw pixels.
  quality.prepass = !quality.mobile && !DETERMINISTIC;
  // Carjack + pull-out assist (vehicles/unpark.ts): play mode only (screenshot mode never runs it), on by default;
  // `?fx=nodrivable` or `?fx=drivable=0` turns it off.
  quality.drivable = true;
  // ?fx=pr=1.25,shadow=1024,draw=700,nossr,noglare,noao,nobloom,noshadows — performance experiments on one tier
  // (measured A/B on the same machine); never set by the game itself.
  const fx = typeof location !== 'undefined' ? new URLSearchParams(location.search).get('fx') : null;
  for (const token of fx ? fx.split(',') : []) {
    const [key, value] = token.split('=');
    if (key === 'pr' && Number(value) > 0) quality.pixelRatio = Number(value);
    else if (key === 'shadow' && Number(value) > 0) quality.shadowMapSize = Number(value);
    else if (key === 'draw' && Number(value) > 0) quality.drawDistance = Number(value);
    else if (key === 'nossr') quality.ssr = false;
    else if (key === 'noglare') quality.glare = false;
    else if (key === 'noao') quality.ssao = false;
    else if (key === 'nobloom') quality.bloom = false;
    else if (key === 'noshadows') quality.shadows = false;
    else if (key === 'prepass') quality.prepass = true;
    else if (key === 'noprepass') quality.prepass = false;
    else if (key === 'prepassinst') quality.prepassInstanced = true;
    else if (key === 'drivable') quality.drivable = value !== '0' && value !== 'off';
    else if (key === 'nodrivable') quality.drivable = false;
    else if (key === 'peds' && Number(value) >= 0) quality.maxPeds = Number(value);
    else if (key === 'traffic' && Number(value) >= 0) quality.maxTraffic = Number(value);
    else if (key === 'noaa') quality.aa = 'none';
    else if (key === 'msaa4') quality.aa = 'msaa4';
    else if (key === 'msaa2') quality.aa = 'msaa2';
    else if (key === 'smaamedium') quality.aa = 'smaa-medium';
  }
  if (level === 'mobile') {
    quality.pixelRatio = mobilePixelRatio(override === 'low' ? .6 : quality.pixelRatio, innerWidth, innerHeight);
    if (override === 'low') { quality.maxPeds = 6; quality.maxTraffic = 4; quality.farDistance = isIOS() ? quality.drawDistance : 1000; }
  }
  return { quality, device, auto };
}

/** Shared live tier mutation for Settings and the frame-rate governor. Texture
 * selection is captured separately at boot; no asset reload belongs here. */
export function apply(quality: Quality, level: QualityLevel, dpr: number, preserveCity = false): void {
  const next = buildQuality(level, dpr);
  // `mobile` describes the device/UI policy, not a desktop governor's floor.
  const mobile = quality.mobile;
  if (preserveCity) {
    // Automatic recovery keeps traffic/collision ownership and the near tile set
    // stable. An explicit Settings choice can change these caps in place.
    next.drawDistance = quality.drawDistance;
    next.maxTraffic = quality.maxTraffic;
  }
  Object.assign(quality, next, { mobile });
}
